<script setup lang="ts">
import { computed } from 'vue';
import { BorrowerTypeSelection, BorrowerTypeSelectionOptions } from '@/helpers/const';
import type { BorrowerTypeSelectionEnum } from '@/helpers/const';

const props = defineProps<{
  modelValue: BorrowerTypeSelectionEnum | undefined;
  isCommercial: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: BorrowerTypeSelectionEnum | undefined): void;
}>();

// Type guard to check if value has a 'value' property
const hasValueProperty = (value: unknown): value is { value: unknown } => {
  return typeof value === 'object' && value !== null && 'value' in value;
};

const borrowerTypeOptions = computed((): Record<string, string> => {
  if (props.isCommercial) {
    return Object.fromEntries(
      Object.entries(BorrowerTypeSelectionOptions).filter(([k]) => k !== BorrowerTypeSelection.INDIVIDUALS),
    );
  }
  return { ...BorrowerTypeSelectionOptions };
});

// Type guard for valid BorrowerTypeSelectionEnum
const isValidBorrowerType = (value: unknown): value is BorrowerTypeSelectionEnum => {
  return typeof value === 'string' && Object.values(BorrowerTypeSelection).includes(value as BorrowerTypeSelectionEnum);
};

const selectValue = computed({
  get: () => props.modelValue,
  set: (value: string | Record<string, unknown> | undefined) => {
    if (typeof value === 'string') {
      // Only emit if it's a valid enum value
      if (isValidBorrowerType(value)) {
        emit('update:modelValue', value);
      }
    } else if (value === undefined) {
      emit('update:modelValue', undefined);
    } else if (hasValueProperty(value)) {
      const extractedValue = value.value;
      if (typeof extractedValue === 'string' && isValidBorrowerType(extractedValue)) {
        emit('update:modelValue', extractedValue);
      }
    }
  },
});
</script>

<template>
  <div class="my-6 gap-4">
    <BxCard>
      <h3 class="mb-8 flex items-center justify-between text-lg font-medium leading-6 text-gray-900">
        <span class="flex items-center">
          <span>Are the borrowers individuals, or companies/trusts?</span>
        </span>
      </h3>
      <BxSelect
        :id="'borrower-type-selection'"
        v-model="selectValue"
        :data-test-id="'borrower-type-selection'"
        :list-items="borrowerTypeOptions"
        label="Borrower Type"
        label-type="floating"
        class="form-control"
      />
    </BxCard>
  </div>
</template>
